import React, { useEffect, useState } from 'react';
import Layout from "../../components/layout/Layout";
import "bootstrap/dist/css/bootstrap.min.css";
import {
    Box, Typography, Button, Card, CardContent, Avatar, TextField, IconButton, Dialog
} from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import { useLocation } from "react-router-dom";
import { getData, postData, putData } from '../../services/apiService';
import { useNavigate } from "react-router-dom";
import useFetchIp from "../../hooks/useFetchIp";

const TicketDetails = () => {
    const { ip } = useFetchIp();
    const location = useLocation();
    const navigate = useNavigate();
    const queryParams = new URLSearchParams(location.search);
    const id = queryParams.get("id");
    const [data, setData] = useState([]);
    const [openImage, setOpenImage] = useState(false);
    const [comment, setComment] = useState("");
    const [comments, setComments] = useState([]);

    useEffect(() => {
        fetchData();
        fetchComments();
    }, []);

    const fetchData = async () => {
        try {
            const response = await getData(`/get-ticket-by-id/${id}`);
            setData(response.data);
        } catch (error) {
            console.error("Failed to fetch ticket data", error);
        }
    };

    const fetchComments = async () => {
        try {
            const response = await getData(`/get-comments/${id}`);
            setComments(response.data);
        } catch (error) {
            console.error("Failed to fetch comments", error);
        }
    };

    const saveComment = async () => {
        if (!comment.trim()) return;
        try {
            const response = await postData('/create-comment', { ticket_id: id, comment,  ipAddress: ip });
            console.log("Comment saved:", response.data);
            setComment("");
            fetchComments();
        } catch (error) {
            console.error("Failed to save comment", error);
        }
    };

    const backPage = () => {
        navigate(-1);
    }

    const handleCloseTicket = async (id) => {
        try {
            await putData(`/close-ticket/${id}`, { ipAddress: ip });
            fetchData();
        } catch (error) {
            console.error('Failed to close ticket', error);
        }
    };

    return (
        <Layout>
            <Box className="p-6 bg-gray-100 min-h-screen">
                {/* Ticket Header */}
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: { xs: "flex-start", sm: "center" },
                        flexDirection: { xs: "column", sm: "row" },
                        mb: 3,
                        gap: 2,
                    }}
                >
                    <Box>
                        <Typography variant="h6" fontWeight="bold">
                            Ticket ID
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {data.support_ticket_no}
                        </Typography>
                    </Box>
                    <Box>
                        <Button onClick={() => handleCloseTicket(data._id)}
                            disabled={data.status === "RESOLVED"}
                            variant="contained"
                            sx={{
                                bgcolor: "#5E4AE3",
                                textTransform: "none",
                                borderRadius: "8px",
                                "&:hover": { bgcolor: "#4A38C4" },
                                mr: 1
                            }}
                        >
                            Resolve Ticket
                        </Button>
                        <Button variant="contained"
                            sx={{ bgcolor: "green", textTransform: "none", borderRadius: "8px", "&:hover": { bgcolor: "green" }, }}
                            onClick={() => backPage()}>
                            Back
                        </Button>
                    </Box>
                </Box>

                {/* Ticket Info */}
                <Card sx={{ mb: 4, borderRadius: "12px", p: 2 }}>
                    <CardContent>
                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: { xs: "column", sm: "row" },
                                justifyContent: "space-between",
                                alignItems: "flex-start",
                                gap: 2,
                            }}
                        >
                            {/* Left Side - Ticket Details */}
                            <Box flex={1}>
                                <Typography variant="body2" sx={{ mb: 2 }}>
                                    <strong>Ticket no :</strong> {data.support_ticket_no}
                                </Typography>
                                <Typography variant="body2" sx={{ mb: 2 }}>
                                    <strong>Topic:</strong> {data.title}
                                </Typography>
                                <Typography variant="body2" sx={{ mb: 2 }}>
                                    <strong>Description:</strong> {data.message}
                                </Typography>
                                <Typography variant="body2" sx={{ mb: 2 }}>
                                    <strong>Page Url:</strong> {data.page_url}
                                </Typography>
                                <Typography variant="body2" sx={{ mb: 2, fontWeight: "bold" }}>
                                    Status:{" "}
                                    <Box component="span" sx={{ color: "success.main" }}>
                                        {data.status}
                                    </Box>
                                </Typography>
                                <Typography variant="body2" sx={{ mb: 2 }}>
                                    <strong>Created By:</strong> {data?.created_by?.firstName}
                                </Typography>
                            </Box>

                            {/* Right Side - Image */}
                            <Box
                                sx={{
                                    minWidth: 250,
                                    minHeight: 170,
                                    border: "2px solid #ccc",
                                    borderRadius: "12px",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    bgcolor: "#f9f9f9",
                                    cursor: "pointer"
                                }}
                                onClick={() => setOpenImage(true)} // Open full screen
                            >
                                {data.ticket_image ? (
                                    <img
                                        src={data.ticket_image}
                                        alt="Ticket"
                                        style={{
                                            maxWidth: "100%",
                                            maxHeight: "160px",
                                            borderRadius: "8px",
                                        }}
                                    />
                                ) : (
                                    <Typography variant="body2" color="text.secondary">
                                        No Image
                                    </Typography>
                                )}
                            </Box>
                        </Box>
                    </CardContent>
                </Card>

                {/* Fullscreen Image Dialog */}
                <Dialog
                    open={openImage}
                    onClose={() => setOpenImage(false)}
                    maxWidth="md"
                    fullWidth
                >
                    <Box sx={{ p: 2, textAlign: "center", bgcolor: "black" }}>
                        <img
                            src={data.ticket_image}
                            alt="Full Preview"
                            style={{
                                maxWidth: "100%",
                                maxHeight: "90vh",
                                objectFit: "contain",
                                borderRadius: "12px",
                            }}
                        />
                    </Box>
                </Dialog>

                {/* Support Requests */}
                <Typography variant="h6" fontWeight="bold" mb={2}>
                    Support Requests
                </Typography>

                {/* Dynamic Comments */}
                {comments.map((c, index) => (
                    <Card key={index} sx={{ mb: 3, borderRadius: "12px" }}>
                        <CardContent>
                            <Box display="flex" alignItems="center" mb={1}>
                                <Avatar src={c?.created_by?.profileImage || ""} sx={{ mr: 2 }} />
                                <Box>
                                    <Typography fontWeight="bold">
                                        {c?.created_by?.firstName || "Anonymous"}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        {c?.created_by?.email}
                                    </Typography>
                                </Box>
                                <Box sx={{ ml: "auto", textAlign: "right" }}>
                                    <Typography variant="body2" color="text.secondary">
                                        {new Date(c.createdAt).toLocaleString()}
                                    </Typography>
                                </Box>
                            </Box>
                            <Typography variant="body2" color="text.secondary" mb={1}>
                                {c.comment}
                            </Typography>
                        </CardContent>
                    </Card>
                ))}

                {/* Reply Box */}
                <Card sx={{ borderRadius: "12px" }}>
                    <CardContent >
                        <Box display="flex" alignItems="center" >
                            <TextField disabled={data.status === "RESOLVED"}
                                placeholder="Reply Message"
                                fullWidth
                                variant="outlined"
                                size="small"
                                sx={{ mr: 1 }}
                                multiline
                                minRows={3}
                                maxRows={6}
                                value={comment}
                                onChange={(e) => setComment(e.target.value)}
                            />
                            <IconButton disabled={data.status === "RESOLVED"}
                                sx={{
                                    bgcolor: "#5E4AE3",
                                    color: "white",
                                    alignSelf: "flex-end",
                                    "&:hover": { bgcolor: "#4A38C4" },
                                }}
                                onClick={saveComment}
                            >
                                <SendIcon />
                            </IconButton>
                        </Box>
                    </CardContent>
                </Card>
            </Box>
        </Layout>
    );
};

export default TicketDetails;
